import { defineComponent as _defineComponent } from 'vue'
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import useGlobal from '@/composables/useGlobal'
import { createCheckInScanResource } from '@/services/event-checkin.service'

const readerElementId = 'event-scanner-reader'

const DEDUPE_TIMEOUT_MS = 3000


export default /*#__PURE__*/_defineComponent({
  __name: 'EventScanner',
  props: {
  eventId: {
    type: Number,
    required: true,
  },
  eventTitle: {
    type: String,
    default: '',
  },
  sig: {
    type: String,
    required: true,
  },
  scanApiUrl: {
    type: String,
    required: true,
  },
},
  setup(__props, { expose: __expose }) {
  __expose();

const props = __props

const { $t } = useGlobal()

const cameraStarted = ref(false)
const cameraError = ref('')
const cameras = ref<any[]>([])
const selectedCameraId = ref('')

const torchSupported = ref(false)
const torchOn = ref(false)
const zoomSupported = ref(false)
const zoomRange = reactive({ min: 1, max: 1, step: 0.1 })
const zoomValue = ref(1)

// Таймаутний дедуп по токену: той самий токен ігнорується протягом
// DEDUPE_TIMEOUT_MS після успішного скану (не блокує сканування ІНШИХ
// токенів — зручно для потоку людей на вході). Подвійний POST по тому
// самому токену неможливий: поки isProcessing=true — новий скан ігнорується,
// а після відповіді сервера той самий токен ще DEDUPE_TIMEOUT_MS не приймається.
const isProcessing = ref(false)
const lastScannedToken = ref('')
const lastScannedAt = ref(0)
const result = ref<any>(null)

let html5Qrcode: any = null

const scanResource = createCheckInScanResource(props.scanApiUrl)

const formattedResultTime = computed(() => {
  if (!result.value?.checkInTime) return ''
  return new Date(result.value.checkInTime).toLocaleString(window.language)
})

// Колір оверлею результату: зелений / жовтий / червоний.
const overlayVariant = computed(() => {
  if (result.value?.status === 'checked_in') return 'success'
  if (result.value?.status === 'already') return 'warning'
  return 'error'
})

const extractTokenFromScannedText = (text: string): string | null => {
  const uuidRegex = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/

  const match = text.match(uuidRegex)

  return match ? match[0] : null
}

const getQrBoxSize = () => {
  const minEdge = Math.min(window.innerWidth, window.innerHeight)
  const size = Math.floor(minEdge * 0.7)

  return { width: size, height: size }
}

const applyCapabilities = async () => {
  torchSupported.value = false
  zoomSupported.value = false

  if (!html5Qrcode || typeof html5Qrcode.getRunningTrackCapabilities !== 'function') return

  try {
    const capabilities = html5Qrcode.getRunningTrackCapabilities()

    if (capabilities?.torch) {
      torchSupported.value = true
    }

    if (capabilities?.zoom) {
      zoomSupported.value = true
      zoomRange.min = capabilities.zoom.min || 1
      zoomRange.max = capabilities.zoom.max || 1
      zoomRange.step = capabilities.zoom.step || 0.1
      zoomValue.value = zoomRange.min
    }
  } catch (e) {
    torchSupported.value = false
    zoomSupported.value = false
  }
}

const onScanSuccess = async (decodedText: string) => {
  // Поки показано результат — не перезаписуємо його новими кадрами:
  // оператор має встигнути прочитати підсумок і натиснути «Сканувати наступного».
  if (isProcessing.value || result.value) return

  const token = extractTokenFromScannedText(decodedText)

  // Відсканували сторонній QR (без токена всередині) — раніше функція
  // мовчки виходила, і оператор не розумів, спрацювало чи ні.
  // Тепер показуємо явну помилку на будь-який QR.
  if (!token) {
    const isSameForeignWithinTimeout = (
      decodedText === lastScannedToken.value
      && (Date.now() - lastScannedAt.value) < DEDUPE_TIMEOUT_MS
    )

    if (isSameForeignWithinTimeout) return

    lastScannedToken.value = decodedText
    lastScannedAt.value = Date.now()

    result.value = {
      status: 'error',
      message: $t('Це чужий QR-код — він не використовується в нашій системі'),
    }

    return
  }

  const isSameTokenWithinTimeout = (
    token === lastScannedToken.value
    && (Date.now() - lastScannedAt.value) < DEDUPE_TIMEOUT_MS
  )

  if (isSameTokenWithinTimeout) return

  isProcessing.value = true
  lastScannedToken.value = token
  lastScannedAt.value = Date.now()

  try {
    const res = await scanResource.execute({}, {
      token,
      sig: props.sig,
    })
    const { data } = res
    const item = data?.item || {}

    result.value = {
      status: item.status,
      fullName: item.fullName,
      event: item.event,
      checkInTime: item.checkInTime,
    }
  } catch (e: any) {
    // Бекенд віддає машиночитаний `code` у state помилки — показуємо
    // оператору конкретну причину, а не одне узагальнене повідомлення.
    // Формат standards.drf: {errors: [{state: {code: [{message: '...'}]}}]}
    const messagesByCode: Record<string, string> = {
      foreign_event: $t('Цей QR-код від іншого заходу'),
      unknown_token: $t('Це чужий QR-код — він не використовується в нашій системі'),
      invalid_sig: $t('Посилання сканера недійсне або протерміноване'),
      checkin_disabled: $t('QR-чекін для цього заходу вимкнено'),
    }

    let message = $t('Невалідний QR-код або посилання сканера протерміноване')

    if (!e?.status) {
      message = $t('Не вдалося перевірити QR-код. Перевірте з’єднання')
    } else if (typeof e?.json === 'function') {
      try {
        const body = await e.json()
        const state = (body?.errors || []).find((err: any) => err?.state)?.state
        const code = state?.code?.[0]?.message

        if (code && messagesByCode[code]) {
          message = messagesByCode[code]
        }
      } catch (parseError) {
        // тіло не JSON — лишаємо узагальнене повідомлення
      }
    }

    result.value = {
      status: 'error',
      message,
    }
  } finally {
    isProcessing.value = false
  }
}

const onScanFailure = () => {
  // не обробляємо кожен невдалий кадр — це нормально під час пошуку QR
}

const startScanner = async () => {
  cameraError.value = ''
  cameraStarted.value = false

  try {
    const { Html5Qrcode } = await import('html5-qrcode')

    cameras.value = await Html5Qrcode.getCameras()

    const cameraConfig = selectedCameraId.value
      ? { deviceId: { exact: selectedCameraId.value } }
      : { facingMode: 'environment' }

    html5Qrcode = new Html5Qrcode(readerElementId, {
      experimentalFeatures: {
        useBarCodeDetectorIfSupported: true,
      },
      verbose: false,
    })

    await html5Qrcode.start(
      cameraConfig,
      {
        fps: 10,
        qrbox: getQrBoxSize(),
        disableFlip: false,
        aspectRatio: 1.0,
      },
      onScanSuccess,
      onScanFailure
    )

    if (!selectedCameraId.value) {
      const settings = html5Qrcode.getRunningTrackSettings?.()

      selectedCameraId.value = settings?.deviceId || (cameras.value[0]?.id ?? '')
    }

    cameraStarted.value = true

    await applyCapabilities()
  } catch (e: any) {
    cameraStarted.value = false

    const name = e?.name || ''

    if (name === 'NotAllowedError' || String(e).includes('NotAllowedError')) {
      cameraError.value = $t(
        'Доступ до камери заборонено. Дозвольте використання камери в налаштуваннях браузера'
      )
    } else if (name === 'NotFoundError' || String(e).includes('NotFoundError')) {
      cameraError.value = $t('Камеру не знайдено на цьому пристрої')
    } else {
      cameraError.value = $t('Не вдалося запустити камеру')
    }
  }
}

const stopScanner = async () => {
  if (!html5Qrcode) return

  try {
    if (html5Qrcode.isScanning) {
      await html5Qrcode.stop()
    }
    html5Qrcode.clear()
  } catch (e) {
    // ігноруємо помилки зупинки — компонент все одно демонтується
  } finally {
    html5Qrcode = null
  }
}

const switchCamera = async () => {
  await stopScanner()
  cameraStarted.value = false
  await startScanner()
}

const toggleTorch = async () => {
  if (!html5Qrcode || !torchSupported.value) return

  try {
    await html5Qrcode.applyVideoConstraints({
      advanced: [{ torch: !torchOn.value }],
    })
    torchOn.value = !torchOn.value
  } catch (e) {
    torchSupported.value = false
  }
}

const applyZoom = async () => {
  if (!html5Qrcode || !zoomSupported.value) return

  try {
    await html5Qrcode.applyVideoConstraints({
      advanced: [{ zoom: zoomValue.value }],
    })
  } catch (e) {
    zoomSupported.value = false
  }
}

const scanNext = () => {
  result.value = null
  lastScannedToken.value = ''
  lastScannedAt.value = 0
}

onMounted(() => {
  startScanner()
})

onBeforeUnmount(() => {
  stopScanner()
})

const __returned__ = { props, $t, readerElementId, cameraStarted, cameraError, cameras, selectedCameraId, torchSupported, torchOn, zoomSupported, zoomRange, zoomValue, DEDUPE_TIMEOUT_MS, isProcessing, lastScannedToken, lastScannedAt, result, get html5Qrcode() { return html5Qrcode }, set html5Qrcode(v) { html5Qrcode = v }, scanResource, formattedResultTime, overlayVariant, extractTokenFromScannedText, getQrBoxSize, applyCapabilities, onScanSuccess, onScanFailure, startScanner, stopScanner, switchCamera, toggleTorch, applyZoom, scanNext, ref, reactive, computed, onMounted, onBeforeUnmount, get useGlobal() { return useGlobal }, get createCheckInScanResource() { return createCheckInScanResource } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})